"use client";

import React, { useRef, useEffect } from "react";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";
import {
  Button,
  ButtonProps,
  Fade,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/system";
import chroma from "chroma-js";

interface ButtonContainerProps {
  backgroundColor: string;
  textColor: string;
}

const ButtonContainer = styled("div")<ButtonContainerProps>(
  ({ backgroundColor, textColor }) => ({
    position: "relative",
    height: "100%",
    width: "100%",
    borderRadius: "inherit",
    backgroundColor: backgroundColor,
    backdropFilter: "blur(16px)",
    border: "1px solid rgba(155, 155, 155, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    color: textColor,
    zIndex: 1,
    overflow: "hidden",
    maskImage: "-webkit-radial-gradient(white, black)",
    "&:before": {
      content: '""',
      position: "absolute",
      left: "-16px",
      top: "-16px",
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: "var(--button-glow, transparent)",
      opacity: "var(--button-glow-opacity, 0)",
      transition: "opacity var(--button-glow-duration, 0.5s)",
      filter: "blur(20px)",
      transform:
        "translate(var(--pointer-x, 0px), var(--pointer-y, 0px)) translateZ(0)",
    },
    "&:hover:before": {
      "--button-glow-opacity": "1",
      "--button-glow-duration": "0.35s",
    },
  })
);

const BorderContainer = styled("div")({
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  borderRadius: "inherit",
});

interface AnimatedButtonProps extends Omit<ButtonProps, "borderRadius"> {
  borderRadius?: string;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  glowStart?: string;
  glowEnd?: string;
  backgroundColor?: string;
  textColor?: string;
  gradientColor?: string;
  toolTip?: string;
  download?: boolean;
}

export function AnimatedButton({
  borderRadius = "0.8rem",
  children,
  containerClassName,
  borderClassName,
  duration = 5000,
  glowStart = "rgba(255, 255, 255, 0.3)",
  glowEnd = "rgba(128, 128, 128, 0.65)",
  backgroundColor = "rgb(25, 25, 31)",
  textColor = "white",
  gradientColor = "rgba(255, 255, 255, 0.83)",
  toolTip,
  download = false,
  ...otherProps
}: AnimatedButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const button = buttonRef.current;

    const handlePointerMove = (e: PointerEvent) => {
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      button.style.setProperty("--pointer-x", `${x}px`);
      button.style.setProperty("--pointer-y", `${y}px`);

      const glowColor = chroma.mix(glowStart, glowEnd, x / rect.width).hex();
      button.style.setProperty("--button-glow", glowColor);
    };

    button?.addEventListener("pointermove", handlePointerMove);

    return () => {
      button?.removeEventListener("pointermove", handlePointerMove);
    };
  }, [glowStart, glowEnd]);

  return (
    <Tooltip title={toolTip} TransitionComponent={Fade} TransitionProps={{ timeout: 600 }} placement="top">
      <Button
        ref={buttonRef}
        component={download ? "a" : "button"}
        download={download}
        style={
          {
            borderRadius: borderRadius,
            "--button-glow-start": glowStart,
            "--button-glow-end": glowEnd,
          } as React.CSSProperties
        }
        {...otherProps}
      >
        <BorderContainer>
          <MovingBorder duration={duration} gradientColor={gradientColor} />
        </BorderContainer>
        <ButtonContainer
          backgroundColor={backgroundColor}
          textColor={textColor}
        >
          {children}
        </ButtonContainer>
      </Button>
    </Tooltip>
  );
}

interface MovingBorderProps {
  duration: number;
  gradientColor: string;
}

const MovingBorder: React.FC<MovingBorderProps> = ({
  duration,
  gradientColor,
}) => {
  const rotation = useMotionValue(0);

  useAnimationFrame((time) => {
    rotation.set((time / duration) * 360);
  });

  return (
    <motion.div
      style={{
        position: "absolute",
        top: "-100%",
        left: "-100%",
        right: "-100%",
        bottom: "-100%",
        overflow: "hidden",
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: `conic-gradient(from 0deg, transparent 0deg, transparent 20deg, transparent 40deg, ${gradientColor} 100deg, transparent 150deg, transparent 360deg)`,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          rotate: rotation,
        }}
      />
    </motion.div>
  );
};
